import React, { useEffect, useState } from "react";
import { useHistory } from "react-router-dom";
import { Button, Grid, Headline, SelectablePanel, Type, Utility } from "mesh-component-library";
import _isEmpty from "lodash/isEmpty";
import { ContinueAsPpuModal } from "../continue-as-ppu-modal/ContinueAsPpuModal";
import { useLocale } from "../../contexts/LocaleContext";
import { useScreenBreakpoint } from "../../hooks/useScreenBreakpoint";
import { generatePath } from "../../utilities/RedirectUtils";
import { TRANSLATION_CONSTANTS } from "../../app-consts/translations";
import { LOCKOUT } from "../../app-consts/appConstants";
import { Services } from "../../types/Service";

export type TowToLockoutTriageProps = {
  clearCCardInfo: () => void;
  clearPreviousBenefits: () => void;
  commonBenefits: any;
  commonBenefitsSuccess: (data: any) => void;
  displayCode: string;
  noCommonBenefitsWithPrevious: (data: any) => void;
  serviceInfo: any;
  updateCCardInfo: (data: any) => void;
  updateCurrentLocation: (locationData: any) => void;
  updateLoggedInFlag: (loggedInFlag: string) => void;
  updatePpuFlag: (ppuFlag: string) => void;
  updateSoftServiceDetails: (isTowTo: boolean, type: Services, options: any) => void;
  updateTowReason: (reason: string) => void;
};

export const TowToLockoutTriage = ({
  clearCCardInfo,
  clearPreviousBenefits,
  commonBenefits,
  commonBenefitsSuccess,
  displayCode = "",
  noCommonBenefitsWithPrevious,
  serviceInfo,
  updateCCardInfo,
  updateCurrentLocation,
  updatePpuFlag,
  updateLoggedInFlag,
  updateSoftServiceDetails,
  updateTowReason,
}: TowToLockoutTriageProps) => {
  const getTranslatedText = useLocale();
  const history = useHistory();
  const screenBreakpoint = useScreenBreakpoint();

  const [selectedReason, setSelectedReason] = useState("");
  const [showContinueAsPpuModal, setShowContinueAsPpuModal] = useState(false);

  useEffect(() => {
    const previousBenefits = commonBenefits?.previousBenefits;
    if (!!previousBenefits && previousBenefits?.benefits) {
      updatePpuFlag("N");
      updateLoggedInFlag("Y");
      clearCCardInfo();
      commonBenefitsSuccess(previousBenefits);
      clearPreviousBenefits();
    }
  }, []);

  const routeToDisablementLocation = () => {
    if (selectedReason != "lockout_keys_in_car") {
      updateTowReason(selectedReason);
    }
    updateCurrentLocation({ setFromLocationServices: false });
    history.push(generatePath("disablementlocation"));
  };

  const handleOnClickContinue = () => {
    const isToTow = selectedReason !== "lockout_keys_in_car";
    updateSoftServiceDetails(isToTow, LOCKOUT, { towReason: selectedReason });
  };

  const handleOnClickContinueAsGuest = () => {
    updatePpuFlag("Y");
    updateLoggedInFlag("N");
    updateCCardInfo({ transactionAmount: serviceInfo.originalCost });
    noCommonBenefitsWithPrevious({
      message: "",
      response: commonBenefits,
    });
    setShowContinueAsPpuModal(false);
    routeToDisablementLocation();
  };

  const handleOnClickCancel = () => {
    setShowContinueAsPpuModal(false);
  };

  const TowToLockoutTriageReasons = [
    {
      id: "lockout_keys_in_car",
      reason: getTranslatedText(TRANSLATION_CONSTANTS.LOCKOUT_KEYS_IN_CAR),
    },
    {
      id: "lockout_keys_in_trunk",
      reason: getTranslatedText(TRANSLATION_CONSTANTS.LOCKOUT_KEYS_IN_TRUNK),
    },
    {
      id: "lockout_key_fob_inoperable",
      reason: getTranslatedText(TRANSLATION_CONSTANTS.LOCKOUT_KEY_FOB_INOPERABLE),
    },
    {
      id: "lockout_keys_lost",
      reason: getTranslatedText(TRANSLATION_CONSTANTS.LOCKOUT_KEYS_LOST),
    },
  ];

  return (
    <>
      <Grid verticalAlign="middle" id="tow-triage-page">
        <Grid.Column col={{ sm: 12, md: 4, lg: 4, xl: 4 }} offset={{ md: 4, lg: 4, xl: 4 }}>
          <Utility.Div
            utils={{
              hrLeft: "md",
              hrRight: "md",
              vrTop: "md",
              vrBottom: "md",
            }}
          >
            <Headline
              title={
                <Type variant="darker" align="left" size={screenBreakpoint === "sm" ? "7xl" : "3xl"} weight="semibold">
                  {getTranslatedText(TRANSLATION_CONSTANTS.TOW_TO_LOCKOUT_TRIAGE_TITLE)}
                </Type>
              }
            />
          </Utility.Div>
        </Grid.Column>
        <Grid.Column col={{ sm: 12, md: 4, lg: 4, xl: 4 }} offset={{ md: 4, lg: 4, xl: 4 }}>
          <Utility.Div
            utils={{
              hrLeft: "md",
              hrRight: "md",
            }}
          >
            {TowToLockoutTriageReasons.map(({ id: reasonId, reason }) => (
              <SelectablePanel
                key={`tow-triage-reason-${reasonId}`}
                id={reasonId}
                checked={selectedReason === reasonId}
                hasBorder
                showInput
                text={reason}
                type="radio"
                onChange={(event) => {
                  setSelectedReason(event.currentTarget.id);
                }}
              />
            ))}
          </Utility.Div>
        </Grid.Column>
        <Grid.Column
          col={{ sm: 12, md: 4, lg: 4, xl: 4 }}
          offset={{ md: 4, lg: 4, xl: 4 }}
          utils={{
            vrTop: "md",
          }}
        >
          <Utility.Div
            utils={{
              hrLeft: "md",
              hrRight: "md",
            }}
          >
            <Button
              hasUpgrade
              shape="pill"
              size="lg"
              onClick={handleOnClickContinue}
              isBlock
              disabled={_isEmpty(selectedReason)}
            >
              {getTranslatedText(TRANSLATION_CONSTANTS.CONTINUE, displayCode)}
            </Button>
          </Utility.Div>
        </Grid.Column>
      </Grid>
      <ContinueAsPpuModal
        active={showContinueAsPpuModal}
        content={getTranslatedText(TRANSLATION_CONSTANTS.TOW_REASON_NOT_COVERED_MSG)}
        primaryButton={{
          value: getTranslatedText(TRANSLATION_CONSTANTS.CONTINUE_AS_GUEST),
          onClick: handleOnClickContinueAsGuest,
          testId: "continue-as-guest-primary-button",
        }}
        closeButton={{
          onClick: handleOnClickCancel,
          testId: "continue-as-guest-close-button",
        }}
      />
    </>
  );
};
